import { useCallback, useEffect, useState } from 'react';
import api from '../api/client';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';

const TotpSetupPage = () => {
    const [status, setStatus] = useState({ totp_enabled: false, totp_mfa_enabled: false });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Setup state
    const [setupData, setSetupData] = useState(null); // { secret_b32, otpauth_uri, setup_token }
    const [verifyCode, setVerifyCode] = useState('');
    const [setupLoading, setSetupLoading] = useState(false);

    // Disable state
    const [showDisable, setShowDisable] = useState(false);
    const [disablePassword, setDisablePassword] = useState('');
    const [disableLoading, setDisableLoading] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/auth/totp/status');
            setStatus(res?.data?.data || { totp_enabled: false, totp_mfa_enabled: false });
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to fetch TOTP status');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    const handleSetup = async () => {
        setSetupLoading(true);
        setError('');
        setSuccess('');
        try {
            const res = await api.post('/api/auth/totp/setup');
            setSetupData(res?.data?.data);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to initiate TOTP setup');
        } finally {
            setSetupLoading(false);
        }
    };

    const handleVerifySetup = async (e) => {
        e.preventDefault();
        setSetupLoading(true);
        setError('');
        try {
            await api.post('/api/auth/totp/verify-setup', {
                setup_token: setupData.setup_token,
                totp_code: verifyCode,
            });
            setSuccess('TOTP MFA has been enabled successfully.');
            setSetupData(null);
            setVerifyCode('');
            fetchStatus();
        } catch (err) {
            setError(err?.response?.data?.message || 'Verification failed. Check your code and try again.');
        } finally {
            setSetupLoading(false);
        }
    };

    const handleDisable = async (e) => {
        e.preventDefault();
        setDisableLoading(true);
        setError('');
        setSuccess('');
        try {
            const CryptoJS = (await import('crypto-js')).default;
            const encryptedPassword = CryptoJS.AES.encrypt(
                disablePassword,
                process.env.REACT_APP_ENCRYPT_KEY
            ).toString();
            await api.post('/api/auth/totp/disable', { password: encryptedPassword });
            setSuccess('TOTP MFA has been disabled.');
            setShowDisable(false);
            setDisablePassword('');
            fetchStatus();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to disable TOTP');
        } finally {
            setDisableLoading(false);
        }
    };

    if (loading) {
        return <SkeletonList />;
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="TOTP Authenticator Setup"
                subtitle="Add an extra layer of security using an authenticator app"
            />

            {!status.totp_mfa_enabled && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-6">
                    TOTP MFA is not enabled for your application. Contact your administrator to enable it in the app
                    configuration.
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-sm text-red-600 mb-4">
                    {error}
                </div>
            )}
            {success && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3 text-sm text-emerald-700 mb-4">
                    {success}
                </div>
            )}

            {/* Status Card */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div
                            className={`h-10 w-10 rounded-lg grid place-content-center ${
                                status.totp_enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
                            }`}
                        >
                            <ShieldIcon />
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-slate-900">Authenticator App</div>
                            <div
                                className={`text-xs font-medium ${
                                    status.totp_enabled ? 'text-emerald-600' : 'text-slate-400'
                                }`}
                            >
                                {status.totp_enabled ? 'Enabled' : 'Not configured'}
                            </div>
                        </div>
                    </div>

                    {status.totp_mfa_enabled && !status.totp_enabled && !setupData && (
                        <button
                            onClick={handleSetup}
                            disabled={setupLoading}
                            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
                        >
                            {setupLoading ? 'Setting up...' : 'Enable TOTP'}
                        </button>
                    )}

                    {status.totp_enabled && (
                        <button
                            onClick={() => {
                                setShowDisable(!showDisable);
                                setError('');
                                setSuccess('');
                            }}
                            className="px-4 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50"
                        >
                            Disable TOTP
                        </button>
                    )}
                </div>
            </div>

            {/* Setup Flow — QR Code + Verification */}
            {setupData && (
                <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6 space-y-5">
                    <h2 className="text-lg font-semibold text-slate-900">Step 1: Scan QR Code</h2>
                    <p className="text-sm text-slate-500">
                        Open your authenticator app and scan the QR code below, or manually enter the secret key.
                    </p>

                    <div className="flex justify-center">
                        <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                                setupData.otpauth_uri
                            )}`}
                            alt="TOTP QR Code"
                            className="rounded-lg border border-slate-200"
                            width={200}
                            height={200}
                        />
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                        <div className="text-xs text-slate-500 mb-1">Manual entry key</div>
                        <code className="text-sm font-mono text-slate-900 break-all select-all">
                            {setupData.secret_b32}
                        </code>
                    </div>

                    <h2 className="text-lg font-semibold text-slate-900">Step 2: Verify Code</h2>
                    <p className="text-sm text-slate-500">
                        Enter the 6-digit code from your authenticator app to complete setup.
                    </p>

                    <form onSubmit={handleVerifySetup} className="space-y-3">
                        <input
                            type="text"
                            required
                            value={verifyCode}
                            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="000000"
                            maxLength="6"
                        />
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={setupLoading || verifyCode.length !== 6}
                                className="flex-1 rounded-lg bg-slate-900 text-white font-semibold py-2.5 hover:bg-slate-800 disabled:opacity-60"
                            >
                                {setupLoading ? 'Verifying...' : 'Verify & Enable'}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setSetupData(null);
                                    setVerifyCode('');
                                    setError('');
                                }}
                                className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Disable Confirmation */}
            {showDisable && (
                <div className="bg-white border border-red-200 rounded-xl p-6 mb-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-2">Confirm Disable TOTP</h2>
                    <p className="text-sm text-slate-500 mb-4">
                        Enter your password to confirm disabling TOTP MFA. You will need to set it up again if you want
                        to re-enable it.
                    </p>
                    <form onSubmit={handleDisable} className="space-y-3">
                        <input
                            type="password"
                            required
                            value={disablePassword}
                            onChange={(e) => setDisablePassword(e.target.value)}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="Enter your password"
                        />
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={disableLoading}
                                className="flex-1 rounded-lg bg-red-600 text-white font-semibold py-2.5 hover:bg-red-700 disabled:opacity-60"
                            >
                                {disableLoading ? 'Disabling...' : 'Disable TOTP'}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowDisable(false);
                                    setDisablePassword('');
                                    setError('');
                                }}
                                className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

const ShieldIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="m9 12 2 2 4-4" />
    </svg>
);

export default TotpSetupPage;
